import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  SparklesIcon,
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  CpuChipIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import Funnel from './marketingfunnel';

// ── Navigation items (shared with sidebar) ──
const navigation = [
  { name: 'Dashboard', href: '/leads', icon: HomeIcon },
  { name: 'Research Ideal Customer', href: '/research', icon: UserGroupIcon },
  { name: 'AI Agent', href: '/ai-agent', icon: CpuChipIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Shared accent gradient — the app's signature thread, reused for headings, primary
// actions, and the active nav state so every screen reads as one system.
const ACCENT_GRADIENT = 'linear-gradient(90deg, #4C6FFF, #EC4899 55%, #FBBF24)';

// ── Helper: clean AI text by removing markdown links and URLs ──
function cleanText(str) {
  if (!str) return '';
  return String(str)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\((?:https?:\/\/)?(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:[\/\w-]*)?\)/g, '')
    .replace(/https?:\/\/[^\s]+/g, '')
    .replace(/(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?/g, '')
    .replace(/\s*,\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .trim();
}

// ── Helper: split comma-separated AI text into a clean bullet list ──
function toBullets(str) {
  const cleaned = cleanText(str);
  if (!cleaned) return [];
  return cleaned.split(',').map((item) => item.trim()).filter(Boolean);
}

// ── Reusable Field component ──
const Field = ({ label, value, onChange, placeholder, type = 'text', rows = 3 }) => (
  <div>
    <label
      className="funnel-mono mb-1.5 block text-[11px] uppercase tracking-wide"
      style={{ color: 'rgba(241,244,249,0.45)' }}
    >
      {label}
    </label>
    {type === 'textarea' ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="icp-field w-full rounded-xl px-4 py-2.5 text-sm outline-none transition"
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="icp-field w-full rounded-xl px-4 py-2.5 text-sm outline-none transition"
      />
    )}
  </div>
);

// ── Section component — glass card with a colored accent chip header ──
const Section = ({ title, icon, color, children }) => (
  <div
    className="mb-6 overflow-hidden rounded-2xl border backdrop-blur-sm"
    style={{ borderColor: hexToRgba(color, 0.25), background: 'rgba(255,255,255,0.015)' }}
  >
    <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: `1px solid ${hexToRgba(color, 0.15)}` }}>
      <span
        className="flex h-8 w-8 items-center justify-center rounded-lg text-base flex-shrink-0"
        style={{ background: hexToRgba(color, 0.15) }}
      >
        {icon}
      </span>
      <h2 className="funnel-display text-base font-semibold" style={{ color: '#F1F4F9' }}>
        {title}
      </h2>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const COLOR_HEX = {
  blue: '#3B82F6',
  indigo: '#6366F1',
  purple: '#A855F7',
  pink: '#EC4899',
  amber: '#F59E0B',
  red: '#EF4444',
  emerald: '#10B981',
  orange: '#F97316',
  cyan: '#06B6D4',
  teal: '#14B8A6',
  violet: '#8B5CF6',
  rose: '#F43F5E',
  fuchsia: '#D946EF',
  lime: '#84CC16',
  sky: '#0EA5E9',
  slate: '#94A3B8',
};

// ── Card grid for displaying the AI report (glass card + colored top rail) ──
function ReportCards({ data }) {
  const displayKeys = [
    { label: 'Report', key: 'report', color: 'blue', list: false },
    { label: 'Industries', key: 'industries', color: 'indigo', list: true },
    { label: 'Company Size', key: 'companySize', color: 'purple', list: true },
    { label: 'Locations', key: 'locations', color: 'pink', list: true },
    { label: 'Decision Makers', key: 'decisionMakers', color: 'amber', list: true },
    { label: 'Pain Points', key: 'painPoints', color: 'red', list: true },
    { label: 'Trends', key: 'trends', color: 'emerald', list: true },
    { label: 'Competitors', key: 'competitors', color: 'orange', list: true },
    { label: 'Website Research', key: 'websiteResearch', color: 'cyan', list: false },
    { label: 'Plan', key: 'plan', color: 'teal', list: false },
    { label: 'Product Fit', key: 'productFit', color: 'violet', list: false },
    { label: 'Priority Segments', key: 'prioritySegments', color: 'rose', list: true },
    { label: 'Buying Triggers', key: 'buyingTriggers', color: 'fuchsia', list: true },
    { label: 'Objections', key: 'objections', color: 'lime', list: true },
    { label: 'Decision Criteria', key: 'decisionCriteria', color: 'sky', list: true },
    { label: 'Customer Characteristics', key: 'customerCharacteristics', color: 'slate', list: true },
    { label: 'Confidence', key: 'confidence', color: 'amber', list: false },
    { label: 'Research Summary', key: 'researchSummary', color: 'emerald', list: false },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {displayKeys.map((item) => {
        const color = COLOR_HEX[item.color] || COLOR_HEX.blue;
        const bullets = item.list ? toBullets(data?.[item.key]) : [];
        return (
          <div
            key={item.key}
            className="group flex flex-col overflow-hidden rounded-2xl border transition-all hover:-translate-y-0.5"
            style={{
              borderColor: hexToRgba(color, 0.2),
              background: hexToRgba(color, 0.04),
            }}
          >
            <div className="h-[3px] w-full" style={{ background: color }} />
            <div className="px-4 pt-3 pb-1">
              <h3
                className="funnel-mono text-[11px] font-medium uppercase tracking-widest"
                style={{ color }}
              >
                {item.label}
              </h3>
            </div>
            <div className="flex-1 p-4 pt-2">
              {item.list ? (
                bullets.length ? (
                  <ul className="flex flex-wrap gap-1.5">
                    {bullets.map((bullet, idx) => (
                      <li key={idx}>
                        <span
                          className="inline-block break-words rounded-full px-2.5 py-1 text-xs font-medium"
                          style={{ background: hexToRgba(color, 0.14), color }}
                        >
                          {bullet}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm" style={{ color: 'rgba(241,244,249,0.25)' }}>—</p>
                )
              ) : (
                <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: 'rgba(241,244,249,0.65)' }}>
                  {cleanText(data?.[item.key]) || '—'}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ICP({ embedded = false, onClose } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [reportData, setReportData] = useState(null);
  const [existingIcp, setExistingIcp] = useState(null);

  const [companyData, setCompanyData] = useState({
    CompanyName: '',
    Website: '',
    Industry: '',
  });

  const STORAGE_KEY = 'icp_draft';

  const initialFormState = {
    targetIndustries: '',
    targetCompanySizes: '',
    targetGeographicRegion: '',
    targetJobTitles: '',
    targetSeniorityLevel: '',
    criticalProblem: '',
    primaryPainPoints: '',
    biggestFrustrations: '',
    impactOfProblem: '',
    desiredOutcomes: '',
    competitorInfo: '',
    uniqueDifferentiators: '',
    competitorWeaknesses: '',
    marketTrends: '',
    decisionMakerRole: '',
    stakeholderInvolvement: '',
    decisionCriteria: '',
    budgetRange: '',
    decisionTimeline: '',
    keySuccessMetrics: '',
    valueProposition: '',
    expectedROI: '',
    whyTheyChooseUs: '',
    averageDealSize: '',
    researchInstructions: '',
  };

  const [form, setForm] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return initialFormState;
      }
    }
    return initialFormState;
  });

  // ── Fetch company profile and existing ICP ──
  const fetchCompanyData = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/leads/company', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch company profile');
      const payload = await response.json();
      const row = payload?.data?.[0] || payload?.data || {};

      setCompanyData({
        CompanyName: String(row.CompanyName ?? ''),
        Website: String(row.Website ?? ''),
        Industry: String(row.Industry ?? ''),
      });

      if (row.ICP) {
        try {
          const icp = JSON.parse(row.ICP);
          if (icp && icp.aiResult) {
            setExistingIcp(icp);
          } else {
            setExistingIcp(null);
          }
        } catch {
          setExistingIcp(null);
        }
      } else {
        setExistingIcp(null);
      }

      setLoading(false);
    } catch (err) {
      console.error(err);
      setError('Unable to load company data.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
  }, []);

  // ── Handlers ──
  const handleChange = (field, value) => {
    setForm((prev) => {
      const newForm = { ...prev, [field]: value };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newForm));
      return newForm;
    });
  };

  const handleGenerate = async () => {
    setSaving(true);
    setError('');
    setFeedback('');
    try {
      const payload = {
        ...form,
        companyName: companyData.CompanyName,
        website: companyData.Website,
        companyIndustry: companyData.Industry,
        timestamp: new Date().toISOString(),
      };

      const response = await fetch('https://api.insightos.co.uk/leads/icp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || 'Failed to generate ICP report');
      }

      setReportData(result.data || result.report || []);
      setExistingIcp(null);
      localStorage.removeItem(STORAGE_KEY);
      setFeedback('AI research complete! Review the report below.');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to generate report.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIcp = async () => {
    if (!window.confirm('Are you sure you want to delete your current ICP? This cannot be undone.')) return;
    setDeleting(true);
    setError('');
    setFeedback('');
    try {
      const response = await fetch('https://api.insightos.co.uk/leads/icp', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const result = await response.json();
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || 'Failed to delete ICP');
      }
      setExistingIcp(null);
      setReportData(null);
      setFeedback('ICP deleted. You can now create a new one.');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to delete ICP.');
    } finally {
      setDeleting(false);
    }
  };

  const themeStyles = (
    <style>
      {`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        .icp-root, .icp-root * { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
        .funnel-display { font-family: 'Space Grotesk', ui-sans-serif, system-ui, sans-serif; }
        .funnel-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .icp-field {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          color: #F1F4F9;
        }
        .icp-field::placeholder { color: rgba(241,244,249,0.25); }
        .icp-field:focus { border-color: rgba(76,111,255,0.5); box-shadow: 0 0 0 3px rgba(76,111,255,0.15); }
        .icp-shimmer {
          background: linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.09) 37%, rgba(255,255,255,0.04) 63%);
          background-size: 400% 100%;
          animation: icp-shimmer-move 1.6s ease-in-out infinite;
        }
        @keyframes icp-shimmer-move { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } }
        @media (prefers-reduced-motion: reduce) { .icp-shimmer { animation: none; } }
      `}
    </style>
  );

  const icpBody = (
    <>
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="funnel-mono text-[11px] tracking-[0.2em] uppercase mb-2" style={{ color: 'rgba(255,255,255,0.35)' }}>
            Target audience
          </p>
          <h1
            className="funnel-display text-3xl sm:text-4xl font-semibold bg-clip-text text-transparent"
            style={{ backgroundImage: ACCENT_GRADIENT }}
          >
            Ideal customer profile
          </h1>
          <p className="mt-2 text-sm" style={{ color: 'rgba(241,244,249,0.5)' }}>
            Define who you sell to, then let AI turn it into a research report.
          </p>
        </div>
        {!embedded && (
          <button
            type="button"
            onClick={() => navigate('/leads')}
            className="flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition"
            style={{ borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(241,244,249,0.75)', background: 'rgba(255,255,255,0.03)' }}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to dashboard
          </button>
        )}
      </div>

      {/* Company info banner */}
      <div
        className="mb-8 rounded-2xl border p-5"
        style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}
      >
        <div className="grid gap-5 md:grid-cols-3">
          {[
            { label: 'Company', value: companyData.CompanyName, color: '#4C6FFF' },
            { label: 'Industry', value: companyData.Industry, color: '#EC4899' },
            { label: 'Website', value: companyData.Website, color: '#FBBF24' },
          ].map((f) => (
            <div key={f.label} className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: f.color }} />
              <div>
                <p className="funnel-mono text-[10px] uppercase tracking-widest" style={{ color: 'rgba(241,244,249,0.4)' }}>
                  {f.label}
                </p>
                <p className="funnel-display text-base font-semibold" style={{ color: '#F1F4F9' }}>
                  {f.value || 'Not set'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content logic */}
      {existingIcp ? (
        /* ── Existing ICP view ── */
        <div className="rounded-3xl border p-6 sm:p-8" style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.015)' }}>
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="funnel-display text-xl font-semibold" style={{ color: '#F1F4F9' }}>
                Your current ICP
              </h2>
              <p className="text-sm mt-1" style={{ color: 'rgba(241,244,249,0.45)' }}>
                Generated on {existingIcp.researchMetadata?.researchedAt || 'recently'}
              </p>
            </div>
            <button
              type="button"
              onClick={handleDeleteIcp}
              disabled={deleting}
              className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border transition disabled:opacity-50"
              style={{ borderColor: 'rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.08)', color: '#FCA5A5' }}
            >
              <TrashIcon className="h-4 w-4" />
              {deleting ? 'Deleting…' : 'Delete ICP'}
            </button>
          </div>
          <ReportCards data={existingIcp.aiResult} />
        </div>
      ) : reportData ? (
        /* ── Newly generated report ── */
        <>
          {feedback && (
            <div
              className="mb-6 px-5 py-4 rounded-xl border text-sm"
              style={{ borderColor: 'rgba(52,211,153,0.25)', background: 'rgba(52,211,153,0.08)', color: '#6EE7B7' }}
            >
              {feedback}
            </div>
          )}
          <div className="rounded-3xl border p-6 sm:p-8" style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.015)' }}>
            <ReportCards data={reportData[0] || {}} />
          </div>
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={() => {
                setReportData(null);
                setFeedback('');
                setError('');
              }}
              className="rounded-full border px-5 py-2.5 text-sm font-medium transition"
              style={{ borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(241,244,249,0.75)', background: 'rgba(255,255,255,0.03)' }}
            >
              New research
            </button>
          </div>
        </>
      ) : (
        /* ── Form ── */
        <div>
          {error && (
            <div
              className="mb-6 px-5 py-4 rounded-xl border text-sm"
              style={{ borderColor: 'rgba(248,113,113,0.25)', background: 'rgba(248,113,113,0.08)', color: '#FCA5A5' }}
            >
              {error}
            </div>
          )}
          {feedback && (
            <div
              className="mb-6 px-5 py-4 rounded-xl border text-sm"
              style={{ borderColor: 'rgba(52,211,153,0.25)', background: 'rgba(52,211,153,0.08)', color: '#6EE7B7' }}
            >
              {feedback}
            </div>
          )}

          <Section icon="🎯" title="Target audience" color="#4C6FFF">
            <div className="grid gap-6 md:grid-cols-2">
              <Field label="Target industries" value={form.targetIndustries} onChange={(v) => handleChange('targetIndustries', v)} placeholder="e.g., SaaS, Healthcare Technology, Financial Services" />
              <Field label="Target company sizes" value={form.targetCompanySizes} onChange={(v) => handleChange('targetCompanySizes', v)} placeholder="e.g., 50–200 employees, 500+ Enterprise" />
              <Field label="Geographic regions" value={form.targetGeographicRegion} onChange={(v) => handleChange('targetGeographicRegion', v)} placeholder="e.g., UK, North America, Europe, APAC" />
              <Field label="Job titles" value={form.targetJobTitles} onChange={(v) => handleChange('targetJobTitles', v)} placeholder="e.g., CTO, Marketing Director, VP Sales" />
              <Field label="Seniority level" value={form.targetSeniorityLevel} onChange={(v) => handleChange('targetSeniorityLevel', v)} placeholder="e.g., C-Level, Director, Senior Manager" />
            </div>
          </Section>

          <Section icon="⚠️" title="Pain points & goals" color="#F97316">
            <div className="grid gap-6 md:grid-cols-2">
              <Field label="Critical problem" value={form.criticalProblem} onChange={(v) => handleChange('criticalProblem', v)} type="textarea" placeholder="The single most urgent problem your ideal customer faces" />
              <Field label="Primary pain points" value={form.primaryPainPoints} onChange={(v) => handleChange('primaryPainPoints', v)} type="textarea" placeholder="List their biggest challenges" />
              <Field label="Biggest frustrations" value={form.biggestFrustrations} onChange={(v) => handleChange('biggestFrustrations', v)} type="textarea" placeholder="What makes them angry or frustrated?" />
              <Field label="Impact of the problem" value={form.impactOfProblem} onChange={(v) => handleChange('impactOfProblem', v)} type="textarea" placeholder="How does this problem affect revenue, efficiency, or growth?" />
              <Field label="Desired outcomes" value={form.desiredOutcomes} onChange={(v) => handleChange('desiredOutcomes', v)} type="textarea" placeholder="What does success look like?" />
            </div>
          </Section>

          <Section icon="🏢" title="Competitors & market position" color="#A855F7">
            <div className="grid gap-6 md:grid-cols-2">
              <Field label="Main competitors" value={form.competitorInfo} onChange={(v) => handleChange('competitorInfo', v)} type="textarea" placeholder="Who are your main competitors?" />
              <Field label="Unique differentiators" value={form.uniqueDifferentiators} onChange={(v) => handleChange('uniqueDifferentiators', v)} type="textarea" placeholder="What makes your solution stand out?" />
              <Field label="Competitor weaknesses" value={form.competitorWeaknesses} onChange={(v) => handleChange('competitorWeaknesses', v)} type="textarea" placeholder="Where do competitors fall short?" />
              <Field label="Market trends" value={form.marketTrends} onChange={(v) => handleChange('marketTrends', v)} type="textarea" placeholder="Key trends in your industry" />
            </div>
          </Section>

          <Section icon="💼" title="Buying process & decision makers" color="#10B981">
            <div className="grid gap-6 md:grid-cols-2">
              <Field label="Primary decision maker" value={form.decisionMakerRole} onChange={(v) => handleChange('decisionMakerRole', v)} placeholder="e.g., CTO, Head of Operations, CFO" />
              <Field label="Other stakeholders" value={form.stakeholderInvolvement} onChange={(v) => handleChange('stakeholderInvolvement', v)} placeholder="Who else is involved?" />
              <Field label="Decision criteria" value={form.decisionCriteria} onChange={(v) => handleChange('decisionCriteria', v)} type="textarea" placeholder="What factors matter most?" />
              <Field label="Budget range (GBP)" value={form.budgetRange} onChange={(v) => handleChange('budgetRange', v)} placeholder="e.g., £25,000 – £50,000 per year" />
              <Field label="Decision timeline" value={form.decisionTimeline} onChange={(v) => handleChange('decisionTimeline', v)} placeholder="e.g., 2–3 months" />
            </div>
          </Section>

          <Section icon="📈" title="Success metrics & value" color="#6366F1">
            <div className="grid gap-6 md:grid-cols-2">
              <Field label="Key success metrics" value={form.keySuccessMetrics} onChange={(v) => handleChange('keySuccessMetrics', v)} type="textarea" placeholder="e.g., Revenue growth, time saved" />
              <Field label="Value proposition" value={form.valueProposition} onChange={(v) => handleChange('valueProposition', v)} type="textarea" placeholder="Core value you deliver" />
              <Field label="Expected ROI" value={form.expectedROI} onChange={(v) => handleChange('expectedROI', v)} type="textarea" placeholder="Typical return on investment" />
              <Field label="Why they choose you" value={form.whyTheyChooseUs} onChange={(v) => handleChange('whyTheyChooseUs', v)} type="textarea" placeholder="Top reasons customers select you" />
              <Field label="Average deal size (GBP)" value={form.averageDealSize} onChange={(v) => handleChange('averageDealSize', v)} placeholder="e.g., £25,000 – £50,000" />
            </div>
          </Section>

          <Section icon="🤖" title="AI research instructions" color="#06B6D4">
            <Field label="What should the AI specifically research?" value={form.researchInstructions} onChange={(v) => handleChange('researchInstructions', v)} type="textarea" rows={5} placeholder="Provide any additional focus areas, questions, or target accounts" />
          </Section>

          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={saving}
              className="flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition"
              style={{
                color: '#0B0E14',
                background: saving ? 'rgba(255,255,255,0.08)' : ACCENT_GRADIENT,
                cursor: saving ? 'not-allowed' : 'pointer',
                boxShadow: saving ? 'none' : '0 8px 24px -8px rgba(236,72,153,0.5)',
              }}
            >
              {saving ? (
                <>
                  <span
                    className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"
                    style={{ borderColor: 'rgba(255,255,255,0.4)', borderTopColor: 'transparent' }}
                  />
                  <span style={{ color: 'rgba(255,255,255,0.6)' }}>Generating…</span>
                </>
              ) : (
                <>
                  <SparklesIcon className="h-4 w-4" />
                  Generate AI research
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Marketing funnel */}
      <div className="mt-8">
        <Funnel
          companyCode={companyData.CompanyName}
          industry={companyData.Industry}
          description={form.criticalProblem}
        />
      </div>
    </>
  );

  if (loading) {
    return (
      <div
        className={classNames('icp-root flex items-center justify-center', embedded ? 'py-24' : 'h-screen')}
        style={{ background: embedded ? 'transparent' : 'radial-gradient(120% 140% at 50% -10%, #171D2C 0%, #0B0E14 55%)' }}
      >
        {themeStyles}
        <div className="text-center w-full max-w-md px-6">
          <div className="flex flex-col items-center gap-4">
            <div className="icp-shimmer h-16 w-16 rounded-2xl" />
            <div className="icp-shimmer h-4 w-40 rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  if (embedded) {
    return (
      <div className="icp-root">
        {themeStyles}
        <div className="p-6 lg:p-8 rounded-[28px]" style={{ background: 'radial-gradient(120% 140% at 50% -10%, #171D2C 0%, #0B0E14 55%)' }}>
          <div className="mx-auto w-full max-w-5xl">{icpBody}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="icp-root flex h-screen" style={{ background: '#0B0E14' }}>
      {themeStyles}
      {/* Mobile sidebar */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="fixed top-4 left-4 z-50 rounded-lg p-2 border"
          style={{ background: '#0F131C', borderColor: 'rgba(255,255,255,0.08)' }}
        >
          <Bars3Icon className="size-6" style={{ color: 'rgba(241,244,249,0.7)' }} />
        </button>

        {sidebarOpen && (
          <div className="fixed inset-0 z-50 flex">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
            <div className="relative w-64 shadow-2xl" style={{ background: '#0F131C' }}>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="absolute top-4 right-4 rounded-lg p-2"
                style={{ color: 'rgba(241,244,249,0.6)' }}
              >
                <XMarkIcon className="size-6" />
              </button>
              <SidebarContent location={location} navigate={navigate} />
            </div>
          </div>
        )}
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:block lg:w-64 lg:flex-shrink-0">
        <div className="fixed h-full w-64 border-r" style={{ background: '#0F131C', borderColor: 'rgba(255,255,255,0.06)' }}>
          <SidebarContent location={location} navigate={navigate} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto" style={{ background: 'radial-gradient(120% 60% at 50% -10%, #171D2C 0%, #0B0E14 45%)' }}>
        <div className="p-6 lg:p-8">
          <div className="mx-auto w-full max-w-[90rem]">{icpBody}</div>
        </div>
      </div>
    </div>
  );
}

// ── Sidebar content ──
function SidebarContent({ location, navigate }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 shrink-0 items-center px-6">
        <div className="flex items-center gap-2.5">
          <img src="/avenor.png" alt="Logo" className="size-8 rounded-lg" />
          <span className="funnel-display text-lg font-semibold" style={{ color: '#F1F4F9' }}>
            Avenor AI
          </span>
        </div>
      </div>

      <nav className="flex-1 px-4 pb-4">
        <ul className="space-y-1">
          {navigation.map((item) => {
            const active = location.pathname === item.href;
            return (
              <li key={item.name}>
                <button
                  onClick={() => navigate(item.href)}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors"
                  style={{
                    background: active ? 'rgba(76,111,255,0.12)' : 'transparent',
                    color: active ? '#93A9FF' : 'rgba(241,244,249,0.6)',
                  }}
                >
                  <item.icon className="size-5 shrink-0" style={{ color: active ? '#93A9FF' : 'rgba(241,244,249,0.35)' }} />
                  {item.name}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
